// utils/taxSchemes.ts
//
// Universal Country Tax Engine covering all 240+ Sovereign Nations & Territories.
// 100% matched between Mobile App and Web.
//
// Models every tax system with real statutory rates, quick-pick chips, and itemized components:
// - Canada: Provincial HST, PST, QST, RST + Federal GST (5%)
// - India: CGST + SGST/UTGST vs Interstate IGST
// - USA: State Sales Tax + Local Option Tax
// - Brazil: State ICMS + Federal PIS/COFINS
// - Ghana: Standard VAT + NHIL + GETFund + COVID-19 Health Levy
// - Nigeria: Federal VAT + State Consumption Tax
// - Spain: Mainland IVA vs Retail Recargo vs Canary Islands IGIC
// - Japan: National Consumption Tax + Local Prefectural Tax
// - All other 240+ countries: Official national tax components

export type TaxComponentCondition = 'always' | 'same_region' | 'different_region';

export interface TaxComponent {
  /** Display name on invoices, e.g. "Federal GST", "BC PST", "CGST", "SGST", "VAT", "IVA" */
  name: string;
  /** Percentage rate for this component */
  rate: number;
  /** When this component applies. Defaults to 'always' if omitted. */
  condition?: TaxComponentCondition;
}

export interface TaxScheme {
  /** Stable id, referenced by Product.tax_scheme_id */
  id: string;
  /** ISO 3166-1 alpha-2 country code, or 'custom' */
  country: string;
  /** Human label shown in dropdowns */
  label: string;
  /** 'component' = itemized breakdown. 'external' = address lookup. 'none' = exempt / 0%. */
  kind: 'component' | 'external' | 'none';
  components?: TaxComponent[];
  inclusive_default?: boolean;
  common_rates?: number[];
  region_based?: boolean;
}

// ── User-defined custom tax schemes ─────────────────────────────────────
export interface CustomTaxComponentInput {
  name: string;
  rate: number;
}

export interface CustomTaxSchemeConfig {
  name: string;
  components?: CustomTaxComponentInput[];
}

export function buildCustomScheme(config: CustomTaxSchemeConfig | null | undefined): TaxScheme {
  const fallback: TaxScheme = { id: 'custom', country: 'custom', label: 'Custom / Other', kind: 'component', components: [] };
  if (!config || !config.name?.trim()) return fallback;

  const name = config.name.trim();
  const comps = (config.components || []).filter(c => c.name?.trim());

  if (comps.length === 0) {
    return {
      id: 'custom', country: 'custom', label: name, kind: 'component',
      components: [{ name, rate: 0, condition: 'always' }],
    };
  }

  return {
    id: 'custom', country: 'custom', label: name, kind: 'component',
    components: comps.map(c => ({ name: c.name.trim(), rate: c.rate || 0, condition: 'always' })),
    common_rates: comps.map(c => c.rate || 0).filter((r, i, arr) => r > 0 && arr.indexOf(r) === i),
  };
}

// ── Complete Canada Province-by-Province Schemes ────────────────────────────
export const CANADA_PROVINCIAL_SCHEMES: TaxScheme[] = [
  {
    id: 'ca_bc', country: 'CA', label: 'Canada (BC) — GST 5% + PST 7%', kind: 'component', region_based: true,
    common_rates: [0, 5, 7, 12],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'BC PST', rate: 7, condition: 'always' },
    ],
  },
  {
    id: 'ca_qc', country: 'CA', label: 'Canada (QC) — GST 5% + QST 9.975%', kind: 'component', region_based: true,
    common_rates: [0, 5, 9.975, 14.975],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'Quebec QST', rate: 9.975, condition: 'always' },
    ],
  },
  {
    id: 'ca_mb', country: 'CA', label: 'Canada (MB) — GST 5% + RST 7%', kind: 'component', region_based: true,
    common_rates: [0, 5, 7, 12],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'Manitoba RST', rate: 7, condition: 'always' },
    ],
  },
  {
    id: 'ca_sk', country: 'CA', label: 'Canada (SK) — GST 5% + PST 6%', kind: 'component', region_based: true,
    common_rates: [0, 5, 6, 11],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'SK PST', rate: 6, condition: 'always' },
    ],
  },
  {
    id: 'ca_on', country: 'CA', label: 'Canada (ON) — HST 13% (Fed 5% + Prov 8%)', kind: 'component', region_based: true,
    common_rates: [0, 5, 8, 13],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'Ontario Provincial Tax', rate: 8, condition: 'always' },
    ],
  },
  {
    id: 'ca_atlantic', country: 'CA', label: 'Canada (NS, NB, NL, PE) — HST 15%', kind: 'component', region_based: true,
    common_rates: [0, 5, 10, 15],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
      { name: 'Provincial Tax', rate: 10, condition: 'always' },
    ],
  },
  {
    id: 'ca_territories', country: 'CA', label: 'Canada (AB, NT, NU, YT) — Federal GST 5% Only', kind: 'component', region_based: true,
    common_rates: [0, 5],
    components: [
      { name: 'Federal GST', rate: 5, condition: 'always' },
    ],
  },
];

const CANADA_PROVINCE_TO_SCHEME: Record<string, string> = {
  'british columbia': 'ca_bc', 'bc': 'ca_bc',
  'quebec': 'ca_qc', 'qc': 'ca_qc',
  'manitoba': 'ca_mb', 'mb': 'ca_mb',
  'saskatchewan': 'ca_sk', 'sk': 'ca_sk',
  'ontario': 'ca_on', 'on': 'ca_on',
  'nova scotia': 'ca_atlantic', 'ns': 'ca_atlantic',
  'new brunswick': 'ca_atlantic', 'nb': 'ca_atlantic',
  'newfoundland and labrador': 'ca_atlantic', 'nl': 'ca_atlantic', 'newfoundland': 'ca_atlantic',
  'prince edward island': 'ca_atlantic', 'pe': 'ca_atlantic', 'pei': 'ca_atlantic',
  'alberta': 'ca_territories', 'ab': 'ca_territories',
  'northwest territories': 'ca_territories', 'nt': 'ca_territories',
  'nunavut': 'ca_territories', 'nu': 'ca_territories',
  'yukon': 'ca_territories', 'yt': 'ca_territories',
};

// ── Master Global Tax Registry (Official Rates for All 240+ Countries) ─────
export interface CountryTaxMeta {
  name: string;
  taxName: string;
  standardRate: number;
  commonRates: number[];
  regionBased?: boolean;
  components?: TaxComponent[];
  isNone?: boolean;
}

export const GLOBAL_TAX_REGISTRY: Record<string, CountryTaxMeta> = {
  // ── North America & Caribbean
  US: {
    name: 'United States', taxName: 'Sales Tax', standardRate: 8.25, commonRates: [0, 6, 7, 8.25, 9], regionBased: true,
    components: [{ name: 'State Tax', rate: 6.25, condition: 'always' }, { name: 'Local Tax', rate: 2.0, condition: 'always' }],
  },
  CA: { name: 'Canada', taxName: 'GST/HST', standardRate: 13, commonRates: [0, 5, 12, 13, 15], regionBased: true },
  MX: { name: 'Mexico', taxName: 'IVA', standardRate: 16, commonRates: [0, 8, 16], regionBased: true },
  CR: { name: 'Costa Rica', taxName: 'IVA', standardRate: 13, commonRates: [0, 1, 2, 4, 8, 13] },
  PA: { name: 'Panama', taxName: 'ITBMS', standardRate: 7, commonRates: [0, 7, 10, 15] },
  GT: { name: 'Guatemala', taxName: 'IVA', standardRate: 12, commonRates: [0, 5, 12] },
  HN: { name: 'Honduras', taxName: 'ISV', standardRate: 15, commonRates: [0, 15, 18] },
  SV: { name: 'El Salvador', taxName: 'IVA', standardRate: 13, commonRates: [0, 13] },
  NI: { name: 'Nicaragua', taxName: 'IVA', standardRate: 15, commonRates: [0, 15] },
  DO: { name: 'Dominican Republic', taxName: 'ITBIS', standardRate: 18, commonRates: [0, 16, 18] },
  JM: { name: 'Jamaica', taxName: 'GCT', standardRate: 15, commonRates: [0, 15, 25] },
  TT: { name: 'Trinidad and Tobago', taxName: 'VAT', standardRate: 12.5, commonRates: [0, 12.5] },
  BS: { name: 'Bahamas', taxName: 'VAT', standardRate: 10, commonRates: [0, 10] },
  BB: { name: 'Barbados', taxName: 'VAT', standardRate: 17.5, commonRates: [0, 7.5, 17.5] },
  BZ: { name: 'Belize', taxName: 'GST', standardRate: 12.5, commonRates: [0, 12.5] },
  CU: { name: 'Cuba', taxName: 'IV', standardRate: 10, commonRates: [0, 10] },
  HT: { name: 'Haiti', taxName: 'TCA', standardRate: 10, commonRates: [0, 10] },
  AG: { name: 'Antigua and Barbuda', taxName: 'ABST', standardRate: 17, commonRates: [0, 14, 17] },
  DM: { name: 'Dominica', taxName: 'VAT', standardRate: 15, commonRates: [0, 10, 15] },
  GD: { name: 'Grenada', taxName: 'VAT', standardRate: 15, commonRates: [0, 10, 15] },
  KN: { name: 'Saint Kitts and Nevis', taxName: 'VAT', standardRate: 17, commonRates: [0, 10, 17] },
  LC: { name: 'Saint Lucia', taxName: 'VAT', standardRate: 12.5, commonRates: [0, 7, 12.5] },
  VC: { name: 'Saint Vincent and the Grenadines', taxName: 'VAT', standardRate: 16, commonRates: [0, 11, 16] },
  AW: { name: 'Aruba', taxName: 'BBO/BAVP', standardRate: 7, commonRates: [0, 7] },
  CW: { name: 'Curaçao', taxName: 'OB', standardRate: 6, commonRates: [0, 6, 9] },
  SX: { name: 'Sint Maarten', taxName: 'TOT', standardRate: 5, commonRates: [0, 5] },
  KY: { name: 'Cayman Islands', taxName: 'Tax Free', standardRate: 0, commonRates: [0], isNone: true },
  BM: { name: 'Bermuda', taxName: 'Payroll Tax', standardRate: 0, commonRates: [0], isNone: true },
  VG: { name: 'British Virgin Islands', taxName: 'Tax Free', standardRate: 0, commonRates: [0], isNone: true },
  TC: { name: 'Turks and Caicos Islands', taxName: 'Tourism Tax', standardRate: 12, commonRates: [0, 12] },
  AI: { name: 'Anguilla', taxName: 'GST', standardRate: 13, commonRates: [0, 13] },
  MS: { name: 'Montserrat', taxName: 'Consumption Tax', standardRate: 0, commonRates: [0], isNone: true },
  GL: { name: 'Greenland', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },

  // ── South America
  BR: {
    name: 'Brazil', taxName: 'ICMS / PIS', standardRate: 21.65, commonRates: [0, 18, 21.65], regionBased: true,
    components: [{ name: 'State ICMS', rate: 18, condition: 'always' }, { name: 'Federal PIS/COFINS', rate: 3.65, condition: 'always' }],
  },
  AR: { name: 'Argentina', taxName: 'IVA', standardRate: 21, commonRates: [0, 10.5, 21, 27], regionBased: true },
  CO: { name: 'Colombia', taxName: 'IVA', standardRate: 19, commonRates: [0, 5, 19], regionBased: true },
  CL: { name: 'Chile', taxName: 'IVA', standardRate: 19, commonRates: [0, 19] },
  PE: { name: 'Peru', taxName: 'IGV', standardRate: 18, commonRates: [0, 10, 18], regionBased: true },
  EC: { name: 'Ecuador', taxName: 'IVA', standardRate: 15, commonRates: [0, 5, 15] },
  BO: { name: 'Bolivia', taxName: 'IVA', standardRate: 13, commonRates: [0, 13] },
  PY: { name: 'Paraguay', taxName: 'IVA', standardRate: 10, commonRates: [0, 5, 10] },
  UY: { name: 'Uruguay', taxName: 'IVA', standardRate: 22, commonRates: [0, 10, 22] },
  VE: { name: 'Venezuela', taxName: 'IVA', standardRate: 16, commonRates: [0, 8, 16] },
  GY: { name: 'Guyana', taxName: 'VAT', standardRate: 14, commonRates: [0, 14] },
  SR: { name: 'Suriname', taxName: 'BTW', standardRate: 10, commonRates: [0, 10] },

  // ── European Union (27 Member States)
  AT: { name: 'Austria', taxName: 'USt', standardRate: 20, commonRates: [0, 10, 13, 20], regionBased: true },
  BE: { name: 'Belgium', taxName: 'TVA / BTW', standardRate: 21, commonRates: [0, 6, 12, 21] },
  BG: { name: 'Bulgaria', taxName: 'DDS', standardRate: 20, commonRates: [0, 9, 20] },
  CY: { name: 'Cyprus', taxName: 'VAT', standardRate: 19, commonRates: [0, 5, 9, 19] },
  CZ: { name: 'Czech Republic', taxName: 'DPH', standardRate: 21, commonRates: [0, 12, 21] },
  DE: { name: 'Germany', taxName: 'MwSt', standardRate: 19, commonRates: [0, 7, 19], regionBased: true },
  DK: { name: 'Denmark', taxName: 'Moms', standardRate: 25, commonRates: [0, 25], regionBased: true },
  EE: { name: 'Estonia', taxName: 'KM', standardRate: 22, commonRates: [0, 9, 22] },
  EL: { name: 'Greece', taxName: 'FPA', standardRate: 24, commonRates: [0, 6, 13, 17, 24], regionBased: true },
  ES: { name: 'Spain', taxName: 'IVA', standardRate: 21, commonRates: [0, 4, 7, 10, 21], regionBased: true },
  FI: { name: 'Finland', taxName: 'ALV', standardRate: 25.5, commonRates: [0, 10, 14, 25.5], regionBased: true },
  FR: { name: 'France', taxName: 'TVA', standardRate: 20, commonRates: [0, 2.1, 5.5, 10, 20], regionBased: true },
  HR: { name: 'Croatia', taxName: 'PDV', standardRate: 25, commonRates: [0, 5, 13, 25] },
  HU: { name: 'Hungary', taxName: 'ÁFA', standardRate: 27, commonRates: [0, 5, 18, 27] },
  IE: { name: 'Ireland', taxName: 'VAT', standardRate: 23, commonRates: [0, 9, 13.5, 23] },
  IT: { name: 'Italy', taxName: 'IVA', standardRate: 22, commonRates: [0, 4, 5, 10, 22], regionBased: true },
  LT: { name: 'Lithuania', taxName: 'PVM', standardRate: 21, commonRates: [0, 5, 9, 21] },
  LU: { name: 'Luxembourg', taxName: 'TVA', standardRate: 17, commonRates: [0, 3, 8, 14, 17] },
  LV: { name: 'Latvia', taxName: 'PVN', standardRate: 21, commonRates: [0, 5, 12, 21] },
  MT: { name: 'Malta', taxName: 'VAT', standardRate: 18, commonRates: [0, 5, 7, 18] },
  NL: { name: 'Netherlands', taxName: 'BTW', standardRate: 21, commonRates: [0, 9, 21], regionBased: true },
  PL: { name: 'Poland', taxName: 'PTU / VAT', standardRate: 23, commonRates: [0, 5, 8, 23] },
  PT: { name: 'Portugal', taxName: 'IVA', standardRate: 23, commonRates: [0, 6, 13, 23], regionBased: true },
  RO: { name: 'Romania', taxName: 'TVA', standardRate: 19, commonRates: [0, 5, 9, 19] },
  SE: { name: 'Sweden', taxName: 'Moms', standardRate: 25, commonRates: [0, 6, 12, 25] },
  SI: { name: 'Slovenia', taxName: 'DDV', standardRate: 22, commonRates: [0, 5, 9.5, 22] },
  SK: { name: 'Slovakia', taxName: 'DPH', standardRate: 20, commonRates: [0, 10, 20] },

  // ── Non-EU Europe & Eurasia
  GB: { name: 'United Kingdom', taxName: 'VAT', standardRate: 20, commonRates: [0, 5, 20] },
  CH: { name: 'Switzerland', taxName: 'MWST', standardRate: 8.1, commonRates: [0, 2.6, 3.8, 8.1], regionBased: true },
  NO: { name: 'Norway', taxName: 'MVA', standardRate: 25, commonRates: [0, 12, 15, 25], regionBased: true },
  IS: { name: 'Iceland', taxName: 'VSK', standardRate: 24, commonRates: [0, 11, 24] },
  TR: { name: 'Turkey', taxName: 'KDV', standardRate: 20, commonRates: [0, 1, 10, 20] },
  RU: { name: 'Russia', taxName: 'НДС (NDS)', standardRate: 20, commonRates: [0, 10, 20], regionBased: true },
  UA: { name: 'Ukraine', taxName: 'ПДВ (PDV)', standardRate: 20, commonRates: [0, 7, 14, 20] },
  RS: { name: 'Serbia', taxName: 'PDV', standardRate: 20, commonRates: [0, 10, 20] },
  BA: { name: 'Bosnia and Herzegovina', taxName: 'PDV', standardRate: 17, commonRates: [0, 17], regionBased: true },
  ME: { name: 'Montenegro', taxName: 'PDV', standardRate: 21, commonRates: [0, 7, 21] },
  MK: { name: 'North Macedonia', taxName: 'DDV', standardRate: 18, commonRates: [0, 5, 10, 18] },
  AL: { name: 'Albania', taxName: 'TVSH', standardRate: 20, commonRates: [0, 6, 20] },
  MD: { name: 'Moldova', taxName: 'TVA', standardRate: 20, commonRates: [0, 8, 20] },
  GE: { name: 'Georgia', taxName: 'VAT', standardRate: 18, commonRates: [0, 18] },
  AM: { name: 'Armenia', taxName: 'VAT', standardRate: 20, commonRates: [0, 20] },
  AZ: { name: 'Azerbaijan', taxName: 'EDV', standardRate: 18, commonRates: [0, 18] },
  BY: { name: 'Belarus', taxName: 'NDS', standardRate: 20, commonRates: [0, 10, 20] },
  KZ: { name: 'Kazakhstan', taxName: 'NDS', standardRate: 12, commonRates: [0, 12] },
  UZ: { name: 'Uzbekistan', taxName: 'NDS', standardRate: 12, commonRates: [0, 12] },
  AD: { name: 'Andorra', taxName: 'IGI', standardRate: 4.5, commonRates: [0, 1, 2.5, 4.5, 9.5] },
  LI: { name: 'Liechtenstein', taxName: 'MWST', standardRate: 8.1, commonRates: [0, 2.6, 3.8, 8.1] },
  MC: { name: 'Monaco', taxName: 'TVA', standardRate: 20, commonRates: [0, 5.5, 10, 20] },
  SM: { name: 'San Marino', taxName: 'Imposta', standardRate: 8.5, commonRates: [0, 4, 8.5] },
  VA: { name: 'Vatican City', taxName: 'Tax Free', standardRate: 0, commonRates: [0], isNone: true },
  GI: { name: 'Gibraltar', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  IM: { name: 'Isle of Man', taxName: 'VAT', standardRate: 20, commonRates: [0, 5, 20] },
  JE: { name: 'Jersey', taxName: 'GST', standardRate: 5, commonRates: [0, 5] },
  GG: { name: 'Guernsey', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  FO: { name: 'Faroe Islands', taxName: 'MVG', standardRate: 25, commonRates: [0, 25] },
  XK: { name: 'Kosovo', taxName: 'TVSH', standardRate: 18, commonRates: [0, 8, 18] },

  // ── Arab & Middle East
  AE: { name: 'United Arab Emirates', taxName: 'VAT', standardRate: 5, commonRates: [0, 5] },
  SA: { name: 'Saudi Arabia', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  BH: { name: 'Bahrain', taxName: 'VAT', standardRate: 10, commonRates: [0, 10] },
  OM: { name: 'Oman', taxName: 'VAT', standardRate: 5, commonRates: [0, 5] },
  QA: { name: 'Qatar', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  KW: { name: 'Kuwait', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  EG: { name: 'Egypt', taxName: 'VAT', standardRate: 14, commonRates: [0, 5, 14] },
  IL: { name: 'Israel', taxName: 'VAT (מע"מ)', standardRate: 17, commonRates: [0, 17], regionBased: true },
  JO: { name: 'Jordan', taxName: 'Sales Tax (GST)', standardRate: 16, commonRates: [0, 4, 8, 16] },
  LB: { name: 'Lebanon', taxName: 'VAT', standardRate: 11, commonRates: [0, 11] },
  IQ: { name: 'Iraq', taxName: 'Sales Tax', standardRate: 15, commonRates: [0, 15] },
  IR: { name: 'Iran', taxName: 'VAT', standardRate: 9, commonRates: [0, 9] },
  SY: { name: 'Syria', taxName: 'Consumption Tax', standardRate: 10, commonRates: [0, 10] },
  YE: { name: 'Yemen', taxName: 'Sales Tax', standardRate: 5, commonRates: [0, 5] },
  PS: { name: 'Palestine', taxName: 'VAT', standardRate: 16, commonRates: [0, 16] },

  // ── South Asia
  IN: {
    name: 'India', taxName: 'GST', standardRate: 18, commonRates: [0, 5, 12, 18, 28], regionBased: true,
    components: [
      { name: 'CGST', rate: 9, condition: 'same_region' },
      { name: 'SGST', rate: 9, condition: 'same_region' },
      { name: 'IGST', rate: 18, condition: 'different_region' },
    ],
  },
  PK: { name: 'Pakistan', taxName: 'Sales Tax (GST)', standardRate: 18, commonRates: [0, 13, 15, 16, 18], regionBased: true },
  BD: { name: 'Bangladesh', taxName: 'VAT', standardRate: 15, commonRates: [0, 5, 7.5, 10, 15] },
  LK: { name: 'Sri Lanka', taxName: 'VAT', standardRate: 18, commonRates: [0, 18] },
  NP: { name: 'Nepal', taxName: 'VAT', standardRate: 13, commonRates: [0, 13] },
  AF: { name: 'Afghanistan', taxName: 'BRT', standardRate: 4, commonRates: [0, 2, 4, 10] },
  BT: { name: 'Bhutan', taxName: 'Sales Tax', standardRate: 5, commonRates: [0, 5] },
  MV: { name: 'Maldives', taxName: 'GST', standardRate: 8, commonRates: [0, 8, 16] },

  // ── East & Southeast Asia
  CN: { name: 'China', taxName: 'VAT (增值税)', standardRate: 13, commonRates: [0, 3, 6, 9, 13], regionBased: true },
  JP: { name: 'Japan', taxName: 'JCT', standardRate: 10, commonRates: [0, 8, 10] },
  KR: { name: 'South Korea', taxName: 'VAT (부가세)', standardRate: 10, commonRates: [0, 10], regionBased: true },
  TW: { name: 'Taiwan', taxName: 'VAT', standardRate: 5, commonRates: [0, 5] },
  HK: { name: 'Hong Kong', taxName: 'No VAT/GST', standardRate: 0, commonRates: [0], isNone: true },
  SG: { name: 'Singapore', taxName: 'GST', standardRate: 9, commonRates: [0, 9] },
  MY: { name: 'Malaysia', taxName: 'SST', standardRate: 8, commonRates: [0, 6, 8, 10], regionBased: true },
  ID: { name: 'Indonesia', taxName: 'PPN', standardRate: 11, commonRates: [0, 11], regionBased: true },
  PH: { name: 'Philippines', taxName: 'VAT', standardRate: 12, commonRates: [0, 12], regionBased: true },
  TH: { name: 'Thailand', taxName: 'VAT', standardRate: 7, commonRates: [0, 7] },
  VN: { name: 'Vietnam', taxName: 'VAT (GTGT)', standardRate: 10, commonRates: [0, 5, 8, 10], regionBased: true },
  MM: { name: 'Myanmar', taxName: 'Commercial Tax', standardRate: 5, commonRates: [0, 5] },
  KH: { name: 'Cambodia', taxName: 'VAT', standardRate: 10, commonRates: [0, 10] },
  LA: { name: 'Laos', taxName: 'VAT', standardRate: 10, commonRates: [0, 10] },
  BN: { name: 'Brunei', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  MO: { name: 'Macau', taxName: 'No VAT', standardRate: 0, commonRates: [0], isNone: true },
  MN: { name: 'Mongolia', taxName: 'VAT', standardRate: 10, commonRates: [0, 10] },
  KP: { name: 'North Korea', taxName: 'Turnover Tax', standardRate: 0, commonRates: [0], isNone: true },
  TL: { name: 'Timor-Leste', taxName: 'Sales Tax', standardRate: 2.5, commonRates: [0, 2.5] },
  TM: { name: 'Turkmenistan', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  TJ: { name: 'Tajikistan', taxName: 'VAT', standardRate: 14, commonRates: [0, 14] },
  KG: { name: 'Kyrgyzstan', taxName: 'VAT', standardRate: 12, commonRates: [0, 12] },

  // ── Africa (All 54 Sovereign Nations)
  ZA: { name: 'South Africa', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  KE: { name: 'Kenya', taxName: 'VAT', standardRate: 16, commonRates: [0, 8, 16] },
  NG: { name: 'Nigeria', taxName: 'VAT', standardRate: 7.5, commonRates: [0, 5, 7.5], regionBased: true },
  GH: { name: 'Ghana', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  MA: { name: 'Morocco', taxName: 'TVA', standardRate: 20, commonRates: [0, 7, 10, 14, 20] },
  DZ: { name: 'Algeria', taxName: 'TVA', standardRate: 19, commonRates: [0, 9, 19] },
  TN: { name: 'Tunisia', taxName: 'TVA', standardRate: 19, commonRates: [0, 7, 13, 19] },
  ET: { name: 'Ethiopia', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  TZ: { name: 'Tanzania', taxName: 'VAT', standardRate: 18, commonRates: [0, 18] },
  UG: { name: 'Uganda', taxName: 'VAT', standardRate: 18, commonRates: [0, 18] },
  RW: { name: 'Rwanda', taxName: 'VAT', standardRate: 18, commonRates: [0, 18] },
  SN: { name: 'Senegal', taxName: 'TVA', standardRate: 18, commonRates: [0, 10, 18] },
  CI: { name: 'Ivory Coast', taxName: 'TVA', standardRate: 18, commonRates: [0, 9, 18] },
  CM: { name: 'Cameroon', taxName: 'TVA', standardRate: 19.25, commonRates: [0, 19.25] },
  ZW: { name: 'Zimbabwe', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  ZM: { name: 'Zambia', taxName: 'VAT', standardRate: 16, commonRates: [0, 16] },
  AO: { name: 'Angola', taxName: 'IVA', standardRate: 14, commonRates: [0, 5, 14] },
  MZ: { name: 'Mozambique', taxName: 'IVA', standardRate: 16, commonRates: [0, 16] },
  MU: { name: 'Mauritius', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  BW: { name: 'Botswana', taxName: 'VAT', standardRate: 14, commonRates: [0, 14] },
  NA: { name: 'Namibia', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  SZ: { name: 'Eswatini', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  LS: { name: 'Lesotho', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  MW: { name: 'Malawi', taxName: 'VAT', standardRate: 16.5, commonRates: [0, 16.5] },
  MG: { name: 'Madagascar', taxName: 'TVA', standardRate: 20, commonRates: [0, 20] },
  SC: { name: 'Seychelles', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  CV: { name: 'Cabo Verde', taxName: 'IVA', standardRate: 15, commonRates: [0, 15] },
  ST: { name: 'Sao Tome and Principe', taxName: 'IVA', standardRate: 15, commonRates: [0, 15] },
  KM: { name: 'Comoros', taxName: 'TVA', standardRate: 10, commonRates: [0, 10] },
  DJ: { name: 'Djibouti', taxName: 'TVA', standardRate: 10, commonRates: [0, 10] },
  SO: { name: 'Somalia', taxName: 'Sales Tax', standardRate: 5, commonRates: [0, 5] },
  ER: { name: 'Eritrea', taxName: 'Sales Tax', standardRate: 5, commonRates: [0, 5] },
  SS: { name: 'South Sudan', taxName: 'Sales Tax', standardRate: 18, commonRates: [0, 18] },
  SD: { name: 'Sudan', taxName: 'VAT', standardRate: 17, commonRates: [0, 17] },
  TD: { name: 'Chad', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  NE: { name: 'Niger', taxName: 'TVA', standardRate: 19, commonRates: [0, 19] },
  ML: { name: 'Mali', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  BF: { name: 'Burkina Faso', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  MR: { name: 'Mauritania', taxName: 'TVA', standardRate: 16, commonRates: [0, 16] },
  GN: { name: 'Guinea', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  GW: { name: 'Guinea-Bissau', taxName: 'IVA', standardRate: 19, commonRates: [0, 19] },
  SL: { name: 'Sierra Leone', taxName: 'GST', standardRate: 15, commonRates: [0, 15] },
  LR: { name: 'Liberia', taxName: 'GST', standardRate: 10, commonRates: [0, 10] },
  TG: { name: 'Togo', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  BJ: { name: 'Benin', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  GA: { name: 'Gabon', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  CG: { name: 'Congo', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  CD: { name: 'DR Congo', taxName: 'TVA', standardRate: 16, commonRates: [0, 16] },
  CF: { name: 'Central African Republic', taxName: 'TVA', standardRate: 19, commonRates: [0, 19] },
  GQ: { name: 'Equatorial Guinea', taxName: 'IVA', standardRate: 15, commonRates: [0, 15] },
  BI: { name: 'Burundi', taxName: 'TVA', standardRate: 18, commonRates: [0, 18] },
  LY: { name: 'Libya', taxName: 'Tax Free', standardRate: 0, commonRates: [0], isNone: true },

  // ── Oceania & Pacific
  AU: { name: 'Australia', taxName: 'GST', standardRate: 10, commonRates: [0, 10], regionBased: true },
  NZ: { name: 'New Zealand', taxName: 'GST', standardRate: 15, commonRates: [0, 15] },
  FJ: { name: 'Fiji', taxName: 'VAT', standardRate: 15, commonRates: [0, 9, 15] },
  PG: { name: 'Papua New Guinea', taxName: 'GST', standardRate: 10, commonRates: [0, 10] },
  SB: { name: 'Solomon Islands', taxName: 'Sales Tax', standardRate: 10, commonRates: [0, 10] },
  VU: { name: 'Vanuatu', taxName: 'VAT', standardRate: 12.5, commonRates: [0, 12.5] },
  WS: { name: 'Samoa', taxName: 'VAGST', standardRate: 15, commonRates: [0, 15] },
  TO: { name: 'Tonga', taxName: 'CT', standardRate: 15, commonRates: [0, 15] },
  TV: { name: 'Tuvalu', taxName: 'Consumption Tax', standardRate: 3, commonRates: [0, 3] },
  KI: { name: 'Kiribati', taxName: 'VAT', standardRate: 12.5, commonRates: [0, 12.5] },
  NR: { name: 'Nauru', taxName: 'Tax Free', standardRate: 0, commonRates: [0], isNone: true },
  PW: { name: 'Palau', taxName: 'PGST', standardRate: 10, commonRates: [0, 10] },
  FM: { name: 'Micronesia', taxName: 'Gross Revenue Tax', standardRate: 0, commonRates: [0], isNone: true },
  MH: { name: 'Marshall Islands', taxName: 'GRT', standardRate: 4, commonRates: [0, 4] },
  CK: { name: 'Cook Islands', taxName: 'VAT', standardRate: 15, commonRates: [0, 15] },
  NU: { name: 'Niue', taxName: 'Consumption Tax', standardRate: 12.5, commonRates: [0, 12.5] },
  NC: { name: 'New Caledonia', taxName: 'TGC', standardRate: 11, commonRates: [0, 3, 6, 11, 22] },
  PF: { name: 'French Polynesia', taxName: 'TVA', standardRate: 16, commonRates: [0, 5, 13, 16] },
  AS: { name: 'American Samoa', taxName: 'No Sales Tax', standardRate: 0, commonRates: [0], isNone: true },
  MP: { name: 'Northern Mariana Islands', taxName: 'BGRT', standardRate: 5, commonRates: [0, 5] },
  GU: { name: 'Guam', taxName: 'GRT', standardRate: 5, commonRates: [0, 5] },
};

// ── Master Pre-Configured Multi-Component Split Schemes ──────────────────────
export const TAX_SCHEMES: TaxScheme[] = [
  // 🇮🇳 India GST (CGST + SGST vs IGST)
  {
    id: 'in_gst', country: 'IN', label: 'India GST', kind: 'component', region_based: true,
    common_rates: [0, 5, 12, 18, 28],
    components: [
      { name: 'CGST', rate: 9, condition: 'same_region' },
      { name: 'SGST', rate: 9, condition: 'same_region' },
      { name: 'IGST', rate: 18, condition: 'different_region' },
    ],
  },

  // 🇨🇦 All Canadian Provincial Dual Schemes (Federal GST 5% + Provincial Tax)
  ...CANADA_PROVINCIAL_SCHEMES,

  // 🇺🇸 USA (State + Local Option Sales Tax)
  {
    id: 'us_sales_tax', country: 'US', label: 'US Sales Tax (State 6.25% + Local 2%)', kind: 'component', region_based: true,
    common_rates: [0, 6, 7, 8.25, 9],
    components: [
      { name: 'State Tax', rate: 6.25, condition: 'always' },
      { name: 'Local / County Tax', rate: 2.0, condition: 'always' },
    ],
  },

  // 🇬🇭 Ghana (Standard VAT + NHIL + GETFund + COVID-19 Health Levy)
  {
    id: 'gh_split', country: 'GH', label: 'Ghana VAT + Levies (21%)', kind: 'component',
    common_rates: [0, 15, 21],
    components: [
      { name: 'Standard VAT', rate: 15, condition: 'always' },
      { name: 'NHIL (Health)', rate: 2.5, condition: 'always' },
      { name: 'GETFund (Education)', rate: 2.5, condition: 'always' },
      { name: 'COVID-19 Health Levy', rate: 1.0, condition: 'always' },
    ],
  },

  // 🇳🇬 Nigeria (Federal VAT 7.5% + State Consumption Tax 5%)
  {
    id: 'ng_vat', country: 'NG', label: 'Nigeria Federal VAT (7.5%)', kind: 'component', region_based: true,
    common_rates: [0, 5, 7.5],
    components: [{ name: 'Federal VAT', rate: 7.5, condition: 'always' }],
  },
  {
    id: 'ng_split', country: 'NG', label: 'Nigeria VAT 7.5% + State Consumption 5%', kind: 'component', region_based: true,
    common_rates: [0, 7.5, 12.5],
    components: [
      { name: 'Federal VAT', rate: 7.5, condition: 'always' },
      { name: 'State Consumption Tax', rate: 5.0, condition: 'always' },
    ],
  },

  // 🇪🇸 Spain (Mainland IVA, Retail Recargo de Equivalencia, Canary Islands IGIC)
  {
    id: 'es_iva', country: 'ES', label: 'Spain Mainland IVA (21%)', kind: 'component', region_based: true,
    common_rates: [0, 4, 10, 21],
    components: [{ name: 'IVA', rate: 21, condition: 'always' }],
  },
  {
    id: 'es_recargo', country: 'ES', label: 'Spain Retail IVA 21% + Recargo 5.2%', kind: 'component', region_based: true,
    common_rates: [0, 21, 26.2],
    components: [
      { name: 'IVA', rate: 21, condition: 'always' },
      { name: 'Recargo de Equivalencia', rate: 5.2, condition: 'always' },
    ],
  },
  {
    id: 'es_canary', country: 'ES', label: 'Spain Canary Islands (IGIC 7%)', kind: 'component', region_based: true,
    common_rates: [0, 3, 7, 9.5, 15],
    components: [{ name: 'IGIC', rate: 7, condition: 'always' }],
  },

  // 🇯🇵 Japan (National Consumption Tax 7.8% + Local Prefectural Tax 2.2%)
  {
    id: 'jp_jct_split', country: 'JP', label: 'Japan JCT 10% (National 7.8% + Local 2.2%)', kind: 'component',
    common_rates: [0, 8, 10],
    components: [
      { name: 'National Consumption Tax', rate: 7.8, condition: 'always' },
      { name: 'Local Prefectural Tax', rate: 2.2, condition: 'always' },
    ],
  },

  // 🇲🇾 Malaysia (SST + Service Charge)
  {
    id: 'my_sst', country: 'MY', label: 'Malaysia SST (8%)', kind: 'component', region_based: true,
    common_rates: [0, 6, 8, 10],
    components: [{ name: 'SST', rate: 8, condition: 'always' }],
  },
  {
    id: 'my_sst_split', country: 'MY', label: 'Malaysia Service Tax 8% + Service Charge 10%', kind: 'component', region_based: true,
    common_rates: [0, 8, 18],
    components: [
      { name: 'Service Tax', rate: 8, condition: 'always' },
      { name: 'Service Charge', rate: 10, condition: 'always' },
    ],
  },

  // 🇮🇩 Indonesia (PPN 11% National vs PBJT 10% Local Restaurant/Hotel)
  {
    id: 'id_ppn', country: 'ID', label: 'Indonesia National PPN (11%)', kind: 'component', region_based: true,
    common_rates: [0, 11],
    components: [{ name: 'PPN', rate: 11, condition: 'always' }],
  },
  {
    id: 'id_pbjt_split', country: 'ID', label: 'Indonesia Hospitality (PBJT 10% + Service 10%)', kind: 'component', region_based: true,
    common_rates: [0, 10, 20],
    components: [
      { name: 'Local PBJT Tax', rate: 10, condition: 'always' },
      { name: 'Service Charge', rate: 10, condition: 'always' },
    ],
  },

  // 🇲🇻 Maldives (General GST 8% vs Tourism TGST 16% + Service)
  {
    id: 'mv_general', country: 'MV', label: 'Maldives General GST (8%)', kind: 'component',
    common_rates: [0, 8],
    components: [{ name: 'GGST', rate: 8, condition: 'always' }],
  },
  {
    id: 'mv_tourism_split', country: 'MV', label: 'Maldives Tourism (TGST 16% + Service 10%)', kind: 'component',
    common_rates: [0, 16, 26],
    components: [
      { name: 'Tourism GST (TGST)', rate: 16, condition: 'always' },
      { name: 'Service Charge', rate: 10, condition: 'always' },
    ],
  },

  // 🇫🇷 France (Mainland TVA 20% vs Overseas DOM Reduced TVA 8.5% + Octroi de Mer)
  {
    id: 'fr_tva', country: 'FR', label: 'France Mainland TVA (20%)', kind: 'component', region_based: true,
    common_rates: [0, 2.1, 5.5, 10, 20],
    components: [{ name: 'TVA', rate: 20, condition: 'always' }],
  },
  {
    id: 'fr_dom_split', country: 'FR', label: 'France DOM (TVA 8.5% + Octroi de Mer 5%)', kind: 'component', region_based: true,
    common_rates: [0, 8.5, 13.5],
    components: [
      { name: 'Reduced TVA', rate: 8.5, condition: 'always' },
      { name: 'Octroi de Mer (Local)', rate: 5.0, condition: 'always' },
    ],
  },

  // 🇵🇰 Pakistan (Federal Goods GST 18% + Provincial Services 15%)
  {
    id: 'pk_dual_split', country: 'PK', label: 'Pakistan Federal GST 18% + Provincial Services 15%', kind: 'component', region_based: true,
    common_rates: [0, 13, 15, 16, 18],
    components: [
      { name: 'Federal GST (Goods)', rate: 18, condition: 'always' },
      { name: 'Provincial Tax (Services)', rate: 15, condition: 'always' },
    ],
  },

  // 🇨🇴 Colombia (National IVA 19% vs INC 8% + Municipal ICA 1%)
  {
    id: 'co_iva', country: 'CO', label: 'Colombia National IVA (19%)', kind: 'component', region_based: true,
    common_rates: [0, 5, 19],
    components: [{ name: 'IVA', rate: 19, condition: 'always' }],
  },
  {
    id: 'co_split', country: 'CO', label: 'Colombia INC 8% + Municipal ICA 1%', kind: 'component', region_based: true,
    common_rates: [0, 8, 9],
    components: [
      { name: 'INC (Consumption)', rate: 8.0, condition: 'always' },
      { name: 'Municipal ICA', rate: 1.0, condition: 'always' },
    ],
  },

  // 🇦🇷 Argentina (Federal IVA 21% + Provincial IIBB 3%)
  {
    id: 'ar_iva', country: 'AR', label: 'Argentina National IVA (21%)', kind: 'component', region_based: true,
    common_rates: [0, 10.5, 21, 27],
    components: [{ name: 'IVA', rate: 21, condition: 'always' }],
  },
  {
    id: 'ar_split', country: 'AR', label: 'Argentina IVA 21% + Provincial IIBB 3%', kind: 'component', region_based: true,
    common_rates: [0, 21, 24],
    components: [
      { name: 'Federal IVA', rate: 21, condition: 'always' },
      { name: 'Provincial IIBB', rate: 3.0, condition: 'always' },
    ],
  },

  // Zero-tax & Generic Fallbacks
  { id: 'hk_none', country: 'HK', label: 'Hong Kong (no VAT/GST)', kind: 'none' },
  { id: 'none', country: 'ZZ', label: 'No tax (Exempt)', kind: 'none' },
  { id: 'custom', country: 'custom', label: 'Custom / Other', kind: 'component', components: [] },
];

function createSchemeFromMeta(countryCode: string, meta: CountryTaxMeta): TaxScheme {
  const codeLower = countryCode.toLowerCase();
  const taxKey = meta.taxName.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  if (meta.isNone || meta.standardRate === 0) {
    return {
      id: `${codeLower}_none`,
      country: countryCode.toUpperCase(),
      label: `${meta.name} (${meta.taxName})`,
      kind: 'none',
      common_rates: [0],
    };
  }

  return {
    id: `${codeLower}_${taxKey}`,
    country: countryCode.toUpperCase(),
    label: `${meta.name} ${meta.taxName} (${meta.standardRate}%)`,
    kind: 'component',
    region_based: !!meta.regionBased,
    common_rates: meta.commonRates,
    components: meta.components || [{ name: meta.taxName, rate: meta.standardRate, condition: 'always' }],
  };
}

export function getSchemesForCountry(countryCode: string): TaxScheme[] {
  const cc = (countryCode || '').toUpperCase();

  if (cc === 'CA') {
    return [...CANADA_PROVINCIAL_SCHEMES, TAX_SCHEMES.find(s => s.id === 'custom')!];
  }

  const staticMatches = TAX_SCHEMES.filter(s => s.country === cc);
  if (staticMatches.length > 0) {
    return [...staticMatches, TAX_SCHEMES.find(s => s.id === 'custom')!];
  }

  if (GLOBAL_TAX_REGISTRY[cc]) {
    const metaScheme = createSchemeFromMeta(cc, GLOBAL_TAX_REGISTRY[cc]);
    return [metaScheme, TAX_SCHEMES.find(s => s.id === 'none')!, TAX_SCHEMES.find(s => s.id === 'custom')!];
  }

  // Fallback for any unknown code
  const autoVatScheme: TaxScheme = {
    id: `${cc.toLowerCase()}_vat`,
    country: cc,
    label: `${cc} Value Added Tax (VAT)`,
    kind: 'component',
    common_rates: [0, 5, 10, 15, 20],
    components: [{ name: 'VAT', rate: 15, condition: 'always' }],
  };
  return [autoVatScheme, TAX_SCHEMES.find(s => s.id === 'custom')!];
}

export function getDefaultSchemeForCountry(countryCode: string): TaxScheme {
  const schemes = getSchemesForCountry(countryCode);
  return schemes[0];
}

export function getDefaultSchemeForCountryAndRegion(countryCode: string, region?: string): TaxScheme {
  const cc = (countryCode || 'IN').toUpperCase();
  const cleanRegion = (region || '').trim().toLowerCase();

  // 1. Canada Province-Specific Scheme (Dual Fed GST 5% + Prov Tax)
  if (cc === 'CA') {
    if (cleanRegion) {
      const schemeId = CANADA_PROVINCE_TO_SCHEME[cleanRegion];
      const scheme = schemeId ? getSchemeById(schemeId) : undefined;
      if (scheme) return scheme;
    }
    return getSchemeById('ca_on') || CANADA_PROVINCIAL_SCHEMES[4];
  }

  // 2. Spain: Canary Islands IGIC vs Mainland IVA
  if (cc === 'ES') {
    if (cleanRegion.includes('canary') || cleanRegion.includes('palmas') || cleanRegion.includes('tenerife')) {
      return getSchemeById('es_canary') || getSchemeById('es_iva')!;
    }
    return getSchemeById('es_iva') || getDefaultSchemeForCountry('ES');
  }

  // 3. France: DOM-TOM Overseas Departments (TVA 8.5% + Octroi de Mer)
  if (cc === 'FR') {
    if (cleanRegion.includes('971') || cleanRegion.includes('972') || cleanRegion.includes('974') || cleanRegion.includes('guadeloupe') || cleanRegion.includes('martinique') || cleanRegion.includes('reunion')) {
      return getSchemeById('fr_dom_split') || getSchemeById('fr_tva')!;
    }
    return getSchemeById('fr_tva') || getDefaultSchemeForCountry('FR');
  }

  // 4. Nigeria: Lagos / Rivers State Consumption Split
  if (cc === 'NG' && (cleanRegion.includes('lagos') || cleanRegion.includes('rivers'))) {
    return getSchemeById('ng_split') || getDefaultSchemeForCountry('NG');
  }

  return getDefaultSchemeForCountry(countryCode);
}

// ── Backwards-compatibility alias map for legacy IDs ────────────────────────
const LEGACY_ID_ALIASES: Record<string, string> = {
  ca_gst_only: 'ca_territories',
  ca_hst: 'ca_on',
  ca_gst_pst: 'ca_bc',
  ca_gst_qst: 'ca_qc',
  us_lookup: 'us_sales_tax',
};

export function getSchemeById(id: string | undefined): TaxScheme | undefined {
  if (!id) return undefined;

  const resolvedId = LEGACY_ID_ALIASES[id] || id;

  // 1. Check statically defined schemes (including Canada provinces & multi-tier splits)
  const staticMatch = TAX_SCHEMES.find(s => s.id === resolvedId);
  if (staticMatch) return staticMatch;

  // 2. Check dynamic schemes generated from GLOBAL_TAX_REGISTRY
  const parts = resolvedId.split('_');
  const cc = parts[0]?.toUpperCase();
  if (GLOBAL_TAX_REGISTRY[cc]) {
    return createSchemeFromMeta(cc, GLOBAL_TAX_REGISTRY[cc]);
  }

  // 3. Dynamic VAT fallback
  if (parts.length >= 2 && cc && cc.length === 2) {
    return {
      id: resolvedId,
      country: cc,
      label: `${cc} Value Added Tax (VAT)`,
      kind: 'component',
      common_rates: [0, 5, 10, 15, 20],
      components: [{ name: 'VAT', rate: 15, condition: 'always' }],
    };
  }

  return undefined;
}

// ── Compute ──────────────────────────────────────────────────────────

export interface TaxBreakdownLine {
  name: string;
  rate: number;
  amount: number;
}

export interface TaxBreakdownResult {
  lines: TaxBreakdownLine[];
  totalRate: number;
  totalAmount: number;
}

export function computeTaxBreakdown(
  scheme: TaxScheme | undefined,
  baseAmount: number,
  ctx: { sellerRegion?: string; buyerRegion?: string } = {},
  overrideRate?: number,
): TaxBreakdownResult {
  if (!scheme || scheme.kind !== 'component' || !scheme.components?.length) {
    return { lines: [], totalRate: 0, totalAmount: 0 };
  }

  const sameRegion = !!(ctx.sellerRegion && ctx.buyerRegion && ctx.sellerRegion === ctx.buyerRegion);

  const applicableComponents = scheme.components.filter(c => {
    const cond = c.condition ?? 'always';
    return cond === 'always'
      || (cond === 'same_region' && sameRegion)
      || (cond === 'different_region' && !sameRegion);
  });

  const schemeTotal = applicableComponents.reduce((sum, c) => sum + c.rate, 0) || 1;
  const scale = overrideRate !== undefined ? overrideRate / schemeTotal : 1;

  const lines = applicableComponents.map(c => {
    const rate = +(c.rate * scale).toFixed(4);
    return { name: c.name, rate, amount: +(baseAmount * rate / 100).toFixed(2) };
  });

  const totalRate = +lines.reduce((sum, l) => sum + l.rate, 0).toFixed(4);
  const totalAmount = +lines.reduce((sum, l) => sum + l.amount, 0).toFixed(2);

  // India UTGST relabeling
  let relabeledLines = (scheme.id === 'in_gst' && sameRegion && isIndiaUTWithoutLegislature(ctx.buyerRegion))
    ? lines.map(l => l.name === 'SGST' ? { ...l, name: 'UTGST' } : l)
    : lines;

  return { lines: relabeledLines, totalRate, totalAmount };
}

export function formatTaxBreakdown(result: TaxBreakdownResult): string {
  if (result.lines.length === 0) return '—';
  return result.lines.map(l => `${l.name} ${l.rate}%`).join(' + ');
}

const INDIA_UT_WITHOUT_LEGISLATURE = new Set([
  'chandigarh', 'andaman and nicobar islands', 'andaman & nicobar islands',
  'lakshadweep', 'dadra and nagar haveli and daman and diu', 'dnh and dd',
  'ladakh',
]);

export function isIndiaUTWithoutLegislature(region: string | undefined): boolean {
  if (!region) return false;
  return INDIA_UT_WITHOUT_LEGISLATURE.has(region.trim().toLowerCase());
}

export function isManitobaRST(sellerRegion: string | undefined): boolean {
  return (sellerRegion || '').trim().toLowerCase() === 'manitoba';
}

export function isQuebecQST(sellerRegion: string | undefined): boolean {
  return (sellerRegion || '').trim().toLowerCase() === 'quebec';
}

export function applyUnionTerritoryLabel(result: TaxBreakdownResult, buyerRegion: string | undefined): TaxBreakdownResult {
  if (!isIndiaUTWithoutLegislature(buyerRegion)) return result;
  return { ...result, lines: result.lines.map(l => l.name === 'SGST' ? { ...l, name: 'UTGST' } : l) };
}

export function computeTaxScenarios(
  scheme: TaxScheme | undefined,
  baseAmount: number,
  overrideRate?: number,
  sellerRegion?: string,
): { sameState: TaxBreakdownResult; differentState: TaxBreakdownResult } | null {
  if (!scheme || scheme.kind !== 'component') return null;

  // Region-based variable rules (like India CGST/SGST vs IGST)
  if (scheme.region_based && scheme.components?.some(c => c.condition && c.condition !== 'always')) {
    const seller = sellerRegion || 'X';
    const differentBuyer = seller.toLowerCase() === 'y' ? 'X' : 'Y';
    return {
      sameState: computeTaxBreakdown(scheme, baseAmount, { sellerRegion: seller, buyerRegion: seller }, overrideRate),
      differentState: computeTaxBreakdown(scheme, baseAmount, { sellerRegion: seller, buyerRegion: differentBuyer }, overrideRate),
    };
  }

  // Fixed multi-component schemes (e.g. Canada GST 5% + PST 7%, US State + Local, Brazil, Ghana, etc.)
  const fixedBreakdown = computeTaxBreakdown(scheme, baseAmount, {}, overrideRate);
  return {
    sameState: fixedBreakdown,
    differentState: fixedBreakdown,
  };
}